import { ArrowDownTrayIcon, ArrowPathIcon } from "@heroicons/react/24/outline"
import { useMemo, useState } from "react"
import {
  type AdBlastReportLine,
  type AdLaunchGoal,
  useGetAdBlastReportQuery,
  useListAdLaunchGoalsQuery,
} from "~/ui-rtk/api/adBlastReportsApi"
import { Loader } from "~/ui-rtk/components/ui/common/Loader"
import { Button, DateRangePicker } from "~/ui-rtk/components/ui/controls"
import useDate from "~/ui-rtk/hooks/date"
import { computeAggregate, computeComparison, formatPercent } from "./aggregate"
import AggregateSheet from "./AggregateSheet"
import CompanySheet, { TRACKER_HEADERS, trackerRow } from "./CompanySheet"
import { downloadCsv } from "./csv"
import SheetTabs, { type SheetTab } from "./SheetTabs"

/** Quick ranges offered by the date picker (the widest is capped by the API's 400-day window). */
const QUICK_RANGES = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 14 days", days: 14 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
]

const AGGREGATE_TAB = "__aggregate"

/**
 * Super-admin "Ad Blast Reports": the agency's launch tracker rebuilt from the
 * ad-blast audit history for any date range, laid out like the workbook
 * it replaces — an Aggregate sheet plus one sheet per customer, switched with
 * a spreadsheet-style tab strip above the sheet.
 *
 * The fetch window is widened backwards to the start of last week (relative
 * to the range end) so the Aggregate's "this week / last week" blocks are
 * always complete; the per-company sheets and "recent launches" only show
 * the selected range.
 */
export default function AdBlastReportsPage() {
  const dayjs = useDate()
  // Day-granular range from the shared picker; the query spans whole days.
  const [range, setRange] = useState(() => ({
    from: dayjs().subtract(7, "day").startOf("day").toDate(),
    to: dayjs().endOf("day").toDate(),
  }))
  // Optional comparison period from the picker (null = comparison off).
  const [compare, setCompare] = useState<{ from: Date; to: Date } | null>(null)
  const [activeTab, setActiveTab] = useState(AGGREGATE_TAB)
  // "Now" for the snapshot windows; bumped by Refresh.
  const [anchor, setAnchor] = useState(() => Date.now())
  const rangeValid = range.to > range.from

  const asOf = useMemo(
    () => (range.to.getTime() > anchor ? new Date(anchor) : range.to),
    [range.to, anchor],
  )

  const queryArgs = useMemo(() => {
    const lastWeekStart = dayjs(asOf).startOf("isoWeek").subtract(1, "week")
    const widenedFrom = lastWeekStart.isBefore(range.from)
      ? lastWeekStart.toDate()
      : range.from
    return {
      from: widenedFrom.toISOString(),
      to: range.to.toISOString(),
    }
  }, [dayjs, asOf, range])

  const {
    data: report,
    isFetching,
    isError,
    refetch,
  } = useGetAdBlastReportQuery(queryArgs, { skip: !rangeValid })
  const compareArgs = useMemo(
    () =>
      compare
        ? {
            from: compare.from.toISOString(),
            to: compare.to.toISOString(),
          }
        : null,
    [compare],
  )
  const { data: compareReport, isFetching: isFetchingCompare } =
    useGetAdBlastReportQuery(compareArgs ?? { from: "", to: "" }, {
      skip: !compareArgs,
    })
  const { data: goalRows } = useListAdLaunchGoalsQuery()

  const goals = useMemo(() => {
    const map: Record<string, AdLaunchGoal> = {}
    for (const g of goalRows ?? []) {
      map[g.companyId] = { low: g.low, high: g.high }
    }
    return map
  }, [goalRows])

  const aggregate = useMemo(
    () =>
      report
        ? computeAggregate({
            dayjs,
            lines: report.lines,
            companies: report.companies,
            goals,
            range,
            asOf,
          })
        : null,
    [dayjs, report, goals, range, asOf],
  )

  const linesInRange = useMemo(
    () => aggregate?.recentLaunches ?? [],
    [aggregate],
  )
  const linesByCompany = useMemo(() => {
    const map = new Map<string, AdBlastReportLine[]>()
    for (const line of linesInRange) {
      map.set(line.companyId, [...(map.get(line.companyId) ?? []), line])
    }
    return map
  }, [linesInRange])

  const compareLinesByCompany = useMemo(() => {
    const map = new Map<string, AdBlastReportLine[]>()
    for (const line of compareReport?.lines ?? []) {
      map.set(line.companyId, [...(map.get(line.companyId) ?? []), line])
    }
    return map
  }, [compareReport])

  const comparison = useMemo(
    () =>
      compare && compareReport && report
        ? {
            from: compare.from,
            to: compare.to,
            data: computeComparison(
              report.companies,
              linesInRange,
              compareReport.lines,
            ),
          }
        : undefined,
    [compare, compareReport, report, linesInRange],
  )

  const tabs = useMemo<SheetTab[]>(() => {
    const customers = aggregate?.customers ?? []
    return [
      { key: AGGREGATE_TAB, label: "Aggregate", count: linesInRange.length },
      ...customers.map(c => ({
        key: c.companyId,
        label: c.companyName,
        count: linesByCompany.get(c.companyId)?.length ?? 0,
        alert: c.hasGoal && c.delta < 0,
      })),
    ]
  }, [aggregate, linesInRange.length, linesByCompany])

  const activeKey = tabs.some(t => t.key === activeTab)
    ? activeTab
    : AGGREGATE_TAB
  const activeCompany = report?.companies.find(c => c.id === activeKey)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const handleExport = () => {
    if (!aggregate) return
    const fmt = (iso: string) => dayjs(iso).format("YYYY-MM-DD HH:mm")
    const stamp = dayjs(asOf).format("YYYY-MM-DD")
    if (activeCompany) {
      downloadCsv(
        `ad-launch-tracker-${activeCompany.name.replace(/[^\w-]+/g, "_")}-${stamp}.csv`,
        [...TRACKER_HEADERS],
        (linesByCompany.get(activeCompany.id) ?? []).map(l =>
          trackerRow(l, fmt),
        ),
      )
      return
    }
    downloadCsv(
      `ad-launch-tracker-aggregate-${stamp}.csv`,
      [
        "Customer",
        "Goal low",
        "Goal high",
        "Yesterday launched",
        "Yesterday live",
        "Today launched",
        "Today live",
        "This week launched",
        "This week live",
        "Last week live",
        "Range launched",
        "Range live",
        "Attainment %",
        "Goal status",
        "Channels this week",
        "Latest launch date",
      ],
      aggregate.customers.map(c => [
        c.companyName,
        c.hasGoal ? c.goalLow : "",
        c.hasGoal ? c.goalHigh : "",
        c.yesterday.launched,
        c.yesterday.live,
        c.today.launched,
        c.today.live,
        c.thisWeek.launched,
        c.thisWeek.live,
        c.lastWeek.live,
        c.range.launched,
        c.range.live,
        formatPercent(c.attainment),
        c.goalStatus,
        c.channelsThisWeek.join(", "),
        c.latestLaunchAt ? dayjs(c.latestLaunchAt).format("YYYY-MM-DD") : "",
      ]),
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col p-6">
      <div className="mb-3">
        <h1 className="text-2xl font-bold text-white">Ad Blast Reports</h1>
        <p className="text-sm text-system-gray-400">
          Admin-only launch tracker built from the ad-blast audit history: every
          Marathon blast per customer for the chosen date range.
        </p>
      </div>

      {/* One-row toolbar: the date picker (with compare) and actions, right-aligned. */}
      <div className="mb-4 flex items-center justify-end gap-3">
        <DateRangePicker
          start={range.from}
          end={range.to}
          compareStart={compare?.from}
          compareEnd={compare?.to}
          customPredefinedPeriods={QUICK_RANGES}
          onChange={({ start, end, compareStart, compareEnd }) => {
            setRange({
              from: dayjs(start).startOf("day").toDate(),
              to: dayjs(end).endOf("day").toDate(),
            })
            setCompare(
              compareStart && compareEnd
                ? {
                    from: dayjs(compareStart).startOf("day").toDate(),
                    to: dayjs(compareEnd).endOf("day").toDate(),
                  }
                : null,
            )
          }}
        />
        <Button
          variant={{ variant: "outlined" }}
          size="sm"
          type="button"
          className="flex shrink-0 whitespace-nowrap"
          disabled={!rangeValid || isFetching || isFetchingCompare}
          onClick={() => {
            setAnchor(Date.now())
            refetch()
          }}
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh
        </Button>
        <Button
          variant={{ variant: "solid", color: "pink" }}
          size="sm"
          type="button"
          className="flex shrink-0 whitespace-nowrap"
          disabled={!aggregate}
          onClick={handleExport}
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {!rangeValid && (
        <p className="mb-3 text-sm text-rose-300">
          The range end must be after its start.
        </p>
      )}
      {isError && (
        <p className="mb-3 text-sm text-rose-300">
          Couldn&apos;t load the report.
        </p>
      )}
      {report?.truncated && (
        <p className="mb-3 text-sm text-amber-300">
          The window holds more blasts than the report cap; the oldest were
          dropped. Narrow the range.
        </p>
      )}

      <SheetTabs tabs={tabs} activeKey={activeKey} onChange={setActiveTab} />
      <div className="relative rounded-b-2xl border border-t-0 border-white/10 bg-system-blue-800/40 p-3">
        {/* Centered spinner over the sheet while any report fetch is in flight. */}
        {(isFetching || isFetchingCompare) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-2xl bg-basic-black/60 text-white">
            <Loader size="large" />
          </div>
        )}
        {!aggregate && !isError && (
          <div className="flex min-h-[40vh] items-center justify-center" />
        )}
        {aggregate && report && activeKey === AGGREGATE_TAB && (
          <AggregateSheet
            aggregate={aggregate}
            generatedAt={report.generatedAt}
            timezone={timezone}
            comparison={comparison}
          />
        )}
        {aggregate && activeCompany && (
          <CompanySheet
            company={activeCompany}
            lines={linesByCompany.get(activeCompany.id) ?? []}
            goalStatus={aggregate.customers.find(
              c => c.companyId === activeCompany.id,
            )}
            range={range}
            comparison={
              compare && compareReport
                ? {
                    from: compare.from,
                    to: compare.to,
                    lines: compareLinesByCompany.get(activeCompany.id) ?? [],
                  }
                : undefined
            }
          />
        )}
      </div>
    </div>
  )
}
